import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SiteNav from "../components/Layout/SiteNav";
import { useAuth } from "../contexts/AuthContext";
import {
  BRAND,
  EDITORIAL_FONT,
  PatternTile,
  Eyebrow,
  EditorialShell,
  EditorialCard,
  SerifHeading,
  PillButton,
  useEditorialPalette,
} from "../components/editorial/EditorialPrimitives";
import { submitSolutionsRequest } from "../services/solutionsApi";
import { buildRedirectQuery } from "../utils/safeRedirect";

const DRAFT_KEY = "muslim_solutions_request_draft";
const REQUEST_REDIRECT = buildRedirectQuery("/solutions#solutions-request");
const EMPTY_FORM = {
  businessName: "",
  contactName: "",
  phone: "",
  website: "",
  projectType: "",
  budget: "",
  description: "",
};

function formatPhoneNumber(value) {
  const digits = value.replace(/\D/g, "");
  if (!digits) return "";

  const hasPlus = value.trim().startsWith("+");
  if (hasPlus && !digits.startsWith("1")) return `+${digits.slice(0, 15)}`;

  const hasCountryCode = digits.startsWith("1") && (hasPlus || digits.length > 10);
  const prefix = hasCountryCode ? "+1 " : "";
  const national = (hasCountryCode ? digits.slice(1) : digits).slice(0, 10);
  if (!national) return "+1";
  if (national.length <= 3) return `${prefix}(${national}`;
  if (national.length <= 6) return `${prefix}(${national.slice(0, 3)}) ${national.slice(3)}`;
  return `${prefix}(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
}

function readDraft() {
  try {
    const draft = JSON.parse(sessionStorage.getItem(DRAFT_KEY));
    return {
      formData: Object.fromEntries(
        Object.keys(EMPTY_FORM).map((key) => [
          key,
          typeof draft?.formData?.[key] === "string" ? draft.formData[key] : "",
        ])
      ),
      agreedToTerms: draft?.agreedToTerms === true,
    };
  } catch {
    return { formData: EMPTY_FORM, agreedToTerms: false };
  }
}

const OFFERINGS = [
  {
    t: "Websites",
    b: "A fast, modern site for your restaurant, shop, or practice. Designed, built, and hosted for you, with updates handled by our team.",
  },
  {
    t: "Mobile apps",
    b: "iOS and Android apps for ordering, booking, loyalty, or anything your customers need in their pocket.",
  },
  {
    t: "Custom software",
    b: "Internal tools, dashboards, booking systems, and integrations tailored to how your business actually runs.",
  },
  {
    t: "E-commerce",
    b: "Online stores with payments, inventory, and delivery built in, so you can sell beyond your storefront.",
  },
];

const STEPS = [
  {
    n: "1",
    t: "Send a request",
    b: "Tell us about your business and what you need using the form below.",
  },
  {
    n: "2",
    t: "We meet to scope your project",
    b: "A short call or in-person meeting to understand your goals and map the work.",
  },
  {
    n: "3",
    t: "You receive an offer",
    b: "A clear, fixed offer: a one time down payment now, and a monthly support subscription that only starts when your project goes live.",
  },
  {
    n: "4",
    t: "We build and support it",
    b: "We ship your project, keep it running, and handle changes as your business grows.",
  },
];

const PROJECT_TYPES = [
  "Website",
  "Mobile App",
  "Custom Software",
  "E-commerce",
  "Other",
];

const BUDGETS = [
  "$0 to $500",
  "$500 to $1,000",
  "$1,000 to $5,000",
  "$5,000 to $10,000",
  "$10,000+",
  "Not sure",
];

const inputClass =
  "h-12 rounded-xl border text-[15px] transition-colors focus-visible:ring-0 focus-visible:border-[#bf9a2f] focus-visible:shadow-[0_0_0_3px_rgba(191,154,47,0.15)]";

export default function Solutions() {
  const navigate = useNavigate();
  const palette = useEditorialPalette();
  const { user, loading: authLoading } = useAuth();

  const inputStyle = {
    background: palette.paper,
    borderColor: palette.hairline,
    color: palette.ink,
    fontFamily: EDITORIAL_FONT.sans,
  };
  const labelStyle = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: palette.muted,
    fontFamily: EDITORIAL_FONT.sans,
  };

  const [draft] = useState(readDraft);
  const [formData, setFormData] = useState(() => ({
    ...draft.formData,
    phone: formatPhoneNumber(draft.formData.phone),
  }));
  const [agreedToTerms, setAgreedToTerms] = useState(draft.agreedToTerms);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    try {
      if (submitted) sessionStorage.removeItem(DRAFT_KEY);
      else sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ formData, agreedToTerms }));
    } catch {
      // The form remains usable when browser storage is unavailable.
    }
  }, [formData, agreedToTerms, submitted]);

  useEffect(() => {
    if (window.location.hash === "#solutions-request") {
      document.getElementById("solutions-request")?.scrollIntoView();
    }
  }, []);

  const continueToAuth = (path) => {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ formData, agreedToTerms }));
      navigate(`${path}${REQUEST_REDIRECT}`);
    } catch {
      setError("Please enable browser storage so we can keep your request details while you sign in.");
    }
  };

  const setField = (key, value) =>
    setFormData((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (authLoading || submitting) return;

    if (!formData.businessName.trim()) {
      setError("Please enter your business name.");
      return;
    }
    if (!formData.contactName.trim()) {
      setError("Please enter a contact name.");
      return;
    }
    if (!formData.projectType) {
      setError("Please choose a project type.");
      return;
    }
    if (!formData.description.trim()) {
      setError("Please describe what you need.");
      return;
    }
    if (!agreedToTerms) {
      setError("Please agree to the Service Terms and Privacy Policy.");
      return;
    }

    if (!user || sessionExpired) {
      continueToAuth(sessionExpired ? "/login" : "/signup");
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        businessName: formData.businessName.trim(),
        contactName: formData.contactName.trim(),
        phone: formData.phone.trim(),
        website: formData.website.trim(),
        projectType: formData.projectType,
        budget: formData.budget,
        description: formData.description.trim(),
        agreedToTerms: true,
      };
      const data = await submitSolutionsRequest(payload);
      if (data?.ok === false) {
        setError(data?.error || "Something went wrong. Please try again.");
        return;
      }
      setSubmitted(true);
    } catch (err) {
      console.error("solutions request error", err);
      if (err?.response?.status === 401) {
        setSessionExpired(true);
      } else if (err?.response?.status === 429) {
        setError(
          "You have sent a few requests recently. Please wait a bit and try again."
        );
      } else {
        setError(
          err?.response?.data?.error ||
            "We could not send your request. Please try again."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <EditorialShell>
      <SiteNav />

      {/* ══════════════════════════════════
          HERO
          ══════════════════════════════════ */}
      <section className="relative overflow-hidden" style={{ background: palette.ivory }}>
        <PatternTile opacity={0.05} />
        <div
          className="relative mx-auto px-4 pt-16 pb-14 text-center sm:px-6 sm:pt-24 sm:pb-20 lg:px-10"
          style={{ maxWidth: 1320 }}
        >
          <div className="inline-flex">
            <Eyebrow>Muslim+ Solutions</Eyebrow>
          </div>
          <SerifHeading
            level={1}
            className="mt-6 mb-7"
            style={{
              fontSize: "clamp(2.5rem, 6.5vw, 5.5rem)",
              lineHeight: 0.96,
              letterSpacing: "-0.035em",
            }}
          >
            Software for your business,
            <br />
            <em style={{ color: BRAND.gold, fontWeight: 400 }}>
              built by the Muslim+ team.
            </em>
          </SerifHeading>
          <p
            className="mx-auto mb-8 max-w-xl text-base sm:text-lg md:text-xl"
            style={{ color: palette.ink2, lineHeight: 1.5 }}
          >
            Custom websites, apps, and software for local businesses, built by
            software professionals. One time down payment to start, then a
            monthly support subscription that begins the day you go live. We
            build it, host it, and keep it running so you can focus on your
            customers.
          </p>
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <PillButton
              variant="primary"
              onClick={() =>
                document
                  .getElementById("solutions-request")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Request a project
              <span aria-hidden>→</span>
            </PillButton>
            <PillButton variant="ghost" onClick={() => navigate("/solutions/portal")}>
              Customer portal
            </PillButton>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          WHAT WE BUILD
          ══════════════════════════════════ */}
      <section style={{ background: palette.paper }}>
        <div
          className="mx-auto px-4 py-16 sm:px-6 sm:py-20 lg:px-10"
          style={{ maxWidth: 1320 }}
        >
          <div className="mb-12 text-center lg:text-left">
            <Eyebrow>What we build</Eyebrow>
            <SerifHeading
              className="mt-5"
              style={{
                fontSize: "clamp(2rem, 4.5vw, 3.5rem)",
                letterSpacing: "-0.03em",
              }}
            >
              Real software,
              <em style={{ color: BRAND.gold, fontWeight: 400 }}> not templates.</em>
            </SerifHeading>
          </div>

          <div
            className="grid grid-cols-1 border-t md:grid-cols-2 lg:grid-cols-4"
            style={{ borderColor: palette.hairline }}
          >
            {OFFERINGS.map((o) => (
              <div
                key={o.t}
                className="relative border-b px-6 py-9 sm:px-7 md:odd:border-r"
                style={{ borderColor: palette.hairline }}
              >
                <div className="mb-4">
                  <SerifHeading
                    level={3}
                    style={{ fontSize: 26, fontWeight: 500, lineHeight: 1.1 }}
                  >
                    {o.t}
                  </SerifHeading>
                </div>
                <p className="text-sm" style={{ color: palette.ink2, lineHeight: 1.6 }}>
                  {o.b}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          HOW IT WORKS
          ══════════════════════════════════ */}
      <section
        className="relative overflow-hidden"
        style={{ background: palette.bandBg, color: palette.bandText }}
      >
        <PatternTile opacity={0.04} color={BRAND.gold} />
        <div
          className="relative mx-auto px-4 py-16 sm:px-6 sm:py-20 lg:px-10"
          style={{ maxWidth: 1080 }}
        >
          <Eyebrow color={BRAND.gold}>How it works</Eyebrow>
          <SerifHeading
            className="mt-6"
            style={{
              fontSize: "clamp(1.9rem, 4.5vw, 3.5rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.03em",
            }}
          >
            From first message
            <br />
            <em style={{ color: BRAND.gold, fontWeight: 400 }}>to launch day.</em>
          </SerifHeading>

          <div className="mt-12 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s) => (
              <div key={s.n}>
                <div
                  className="mb-3"
                  style={{
                    fontFamily: EDITORIAL_FONT.serif,
                    fontStyle: "italic",
                    fontSize: 30,
                    color: BRAND.gold,
                    lineHeight: 1,
                  }}
                >
                  {s.n}
                </div>
                <div
                  className="mb-2"
                  style={{
                    fontFamily: EDITORIAL_FONT.serif,
                    fontSize: 20,
                    fontWeight: 500,
                  }}
                >
                  {s.t}
                </div>
                <p className="text-sm" style={{ color: palette.bandMuted, lineHeight: 1.6 }}>
                  {s.b}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          REQUEST FORM
          ══════════════════════════════════ */}
      <section id="solutions-request" style={{ background: palette.ivory }}>
        <div
          className="relative mx-auto px-4 py-16 sm:px-6 sm:py-20 lg:px-10"
          style={{ maxWidth: 860 }}
        >
          <div className="mb-10 space-y-4 text-center">
            <div className="inline-flex">
              <Eyebrow>Start your project</Eyebrow>
            </div>
            <SerifHeading
              level={2}
              style={{
                fontSize: "clamp(2rem, 5vw, 3.5rem)",
                lineHeight: 0.98,
                letterSpacing: "-0.03em",
              }}
            >
              Tell us what
              <em style={{ color: BRAND.gold, fontWeight: 400 }}> you need.</em>
            </SerifHeading>
            <p
              className="mx-auto max-w-xl text-[16px]"
              style={{ color: palette.ink2, lineHeight: 1.55 }}
            >
              Share a few details and our team will reach out to schedule a
              meeting. No commitment until you accept an offer.
            </p>
          </div>

          {submitted ? (
            <EditorialCard variant="ivory" className="p-8 text-center sm:p-12">
              <CheckCircle2
                className="mx-auto mb-5 h-12 w-12"
                style={{ color: BRAND.sage }}
              />
              <SerifHeading
                level={3}
                className="mb-4"
                style={{ fontSize: "clamp(24px, 6vw, 30px)", fontWeight: 500 }}
              >
                Request received.
              </SerifHeading>
              <p
                className="mx-auto mb-8 max-w-md text-[15px]"
                style={{ color: palette.ink2, lineHeight: 1.6 }}
              >
                Check your email for confirmation, our team will reach out to
                schedule a meeting.
              </p>
              <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
                <PillButton variant="primary" onClick={() => navigate("/")}>
                  Back to home
                </PillButton>
                <PillButton variant="ghost" onClick={() => navigate("/solutions/portal")}>
                  Customer portal
                </PillButton>
              </div>
            </EditorialCard>
          ) : (
            <EditorialCard variant="ivory" className="p-6 sm:p-10">
              {error && (
                <Alert
                  className="mb-6"
                  style={{
                    background: BRAND.terracotta + "15",
                    borderColor: BRAND.terracotta + "40",
                    color: BRAND.terracotta,
                  }}
                >
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} autoComplete="on" className="space-y-6" noValidate>
                <p className="break-words text-sm" style={{ color: palette.muted }}>
                  {user && !sessionExpired ? (
                    <>
                      Submitting as{" "}
                      <span style={{ color: palette.ink, fontWeight: 600 }}>
                        {user.email}
                      </span>
                      . We will use this email for your request and customer portal.
                    </>
                  ) : (
                    "Enter your project details first. You will create an account or sign in before sending your request."
                  )}
                </p>
                {sessionExpired && (
                  <p role="status" className="text-sm" style={{ color: palette.ink2 }}>
                    Your session has expired. Your details are still here. Sign in again to send your request.
                  </p>
                )}

                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label htmlFor="businessName" style={labelStyle}>
                      Business name *
                    </label>
                    <Input
                      id="businessName"
                      name="organization"
                      autoComplete="organization"
                      value={formData.businessName}
                      onChange={(e) => setField("businessName", e.target.value)}
                      placeholder="Al Noor Market"
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="contactName" style={labelStyle}>
                      Contact name *
                    </label>
                    <Input
                      id="contactName"
                      name="name"
                      autoComplete="name"
                      value={formData.contactName}
                      onChange={(e) => setField("contactName", e.target.value)}
                      placeholder="Your full name"
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label htmlFor="phone" style={labelStyle}>
                      Phone (optional)
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={formData.phone}
                      onChange={(e) => setField("phone", formatPhoneNumber(e.target.value))}
                      placeholder="(503) 555-0100"
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="website" style={labelStyle}>
                      Current website (optional)
                    </label>
                    <Input
                      id="website"
                      name="website"
                      type="url"
                      inputMode="url"
                      autoComplete="url"
                      autoCapitalize="none"
                      spellCheck={false}
                      value={formData.website}
                      onChange={(e) => setField("website", e.target.value)}
                      placeholder="https://your-current-site.com"
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label style={labelStyle}>Project type *</label>
                    <Select
                      value={formData.projectType}
                      onValueChange={(v) => setField("projectType", v)}
                    >
                      <SelectTrigger className={inputClass} style={inputStyle}>
                        <SelectValue placeholder="Choose a type" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROJECT_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label style={labelStyle}>Budget (optional)</label>
                    <Select
                      value={formData.budget}
                      onValueChange={(v) => setField("budget", v)}
                    >
                      <SelectTrigger className={inputClass} style={inputStyle}>
                        <SelectValue placeholder="Choose a range" />
                      </SelectTrigger>
                      <SelectContent>
                        {BUDGETS.map((b) => (
                          <SelectItem key={b} value={b}>
                            {b}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="description" style={labelStyle}>
                    What do you need? *
                  </label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={(e) => setField("description", e.target.value)}
                    placeholder="Tell us about your business and what you would like us to build."
                    rows={5}
                    className="rounded-xl border text-[15px] transition-colors focus-visible:ring-0 focus-visible:border-[#bf9a2f] focus-visible:shadow-[0_0_0_3px_rgba(191,154,47,0.15)]"
                    style={inputStyle}
                  />
                </div>

                <div className="flex items-start gap-3 pt-1">
                  <Checkbox
                    id="agreedToTerms"
                    checked={agreedToTerms}
                    onCheckedChange={(v) => setAgreedToTerms(v === true)}
                    className="mt-0.5"
                  />
                  <label
                    htmlFor="agreedToTerms"
                    className="cursor-pointer text-sm"
                    style={{ color: palette.ink2, lineHeight: 1.5 }}
                  >
                    I agree to the Muslim+ Solutions{" "}
                    <button
                      type="button"
                      onClick={() => navigate("/solutions/terms")}
                      className="underline transition-colors hover:text-[#bf9a2f]"
                      style={{ color: palette.ink }}
                    >
                      Service Terms
                    </button>{" "}
                    and the{" "}
                    <button
                      type="button"
                      onClick={() => navigate("/policy")}
                      className="underline transition-colors hover:text-[#bf9a2f]"
                      style={{ color: palette.ink }}
                    >
                      Privacy Policy
                    </button>
                    .
                  </label>
                </div>

                {!user && !sessionExpired && (
                  <p className="text-sm" style={{ color: palette.muted }}>
                    Already have a Muslim+ account?{" "}
                    <button
                      type="button"
                      disabled={authLoading}
                      onClick={() => continueToAuth("/login")}
                      className="underline transition-colors hover:text-[#bf9a2f]"
                      style={{ color: palette.ink }}
                    >
                      Sign in to continue
                    </button>
                  </p>
                )}

                <div className="flex flex-col items-center gap-4 pt-2 sm:flex-row sm:justify-between">
                  <p className="text-xs" style={{ color: palette.muted }}>
                    Already a customer?{" "}
                    <button
                      type="button"
                      onClick={() => navigate("/solutions/portal")}
                      className="underline transition-colors hover:text-[#bf9a2f]"
                      style={{ color: palette.ink }}
                    >
                      Sign in to the customer portal
                    </button>
                  </p>
                  <PillButton
                    type="submit"
                    variant="primary"
                    disabled={authLoading || submitting || !agreedToTerms}
                    className="w-full justify-center sm:w-auto"
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        {sessionExpired ? "Sign in to continue" : user ? "Send request" : "Continue to signup"}
                        <span aria-hidden>→</span>
                      </>
                    )}
                  </PillButton>
                </div>
              </form>
            </EditorialCard>
          )}

          <p className="mt-10 text-center text-xs" style={{ color: palette.muted }}>
            By working with Muslim+ Solutions you agree to our{" "}
            <button
              type="button"
              onClick={() => navigate("/solutions/terms")}
              className="underline transition-colors hover:text-[#bf9a2f]"
            >
              Service Terms
            </button>
            .
          </p>
        </div>
      </section>
    </EditorialShell>
  );
}
